import { get } from "lodash";
import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import { getEmailExemptionsTableColumns } from "routes/email/components/email_exemptions_table/EmailExemptionsTableColumnsAndFilters";
import { EMAIL_EXEMPTIONS_TABLE_CONSTANTS } from "routes/email/components/email_exemptions_table/EmailExemptionsTableV3";
import EmailExemptionService from "services/EmailExemptions";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";

export const invalidateEmailExemptionsQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [EMAIL_EXEMPTIONS_TABLE_CONSTANTS.queryKey],
  });
};

export const useEmailExemption = id =>
  useQuery({
    queryKey: ["email-exemption", id],
    queryFn: () => EmailExemptionService.getEmailExemption(id),
    enabled: !!id,
    retry: false,
  });

export const useEmailExemptionsMutations = () => {
  const create = useMutation({
    mutationFn: data => EmailExemptionService.createEmailExemption(data),
    onSuccess: () => invalidateEmailExemptionsQuery(),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) =>
      EmailExemptionService.editEmailExemption(id, data),
    onSuccess: (_, { id }) =>
      Promise.all([
        invalidateEmailExemptionsQuery(),
        queryClient.invalidateQueries({ queryKey: ["email-exemption", id] }),
      ]),
  });

  const remove = useMutation({
    mutationFn: id => EmailExemptionService.deleteEmailExemption(id),
    onSuccess: () => invalidateEmailExemptionsQuery(),
  });

  return { create, update, remove };
};

export const useEmailExemptionsTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getEmailExemptionsTableColumns();
    const emailExemptionsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${EMAIL_EXEMPTIONS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: emailExemptionsTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [tableSettings, isTableSettingsFetched]);

  return { columns };
};
